import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal } from 'antd';
import { d206Api, FD160 } from '../../services/d206Api';
import { lookupApi } from '../../services/lookupApi';
import { useD206Mode } from './hooks/useD206Mode';
import { useD206Record } from './hooks/useD206Record';
import { useD206Navigation } from './hooks/useD206Navigation';
import { useD206Calculation } from './hooks/useD206Calculation';
import { useD206Approval } from './hooks/useD206Approval';
import { useModulePermission } from '../../hooks/usePermission';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Detail from './components/Tab2Detail';
import { SharedActionBar } from '../../components/SharedActionBar';
import { BatchAcceptanceBridge, pwd206f1AcceptanceManifest } from '../../features/acceptance';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import { getStatusBadge, isFieldDisabledFn, isLockedForEdit } from './helpers';
import './D206Form.css';

const D206Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [activeTab, setActiveTab] = useState(0);

  // ── Lookup display names ──
  const [subcontractorName, setSubcontractorName] = useState('');
  const [purchaseReceiptName, setPurchaseReceiptName] = useState('');
  const [warehouseName, setWarehouseName] = useState('');

  // ── Hooks ──
  const perms = useModulePermission(Modules.D206);
  const { mode, setMode, isEditing } = useD206Mode();
  const {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    deleteDetailRow,
    revertToRecord, resetForAdd, applyResult, importReceiptStock,
    getFieldError, clearFieldErrors,
  } = useD206Record();
  const { calcDetailAmount, calcSummary } = useD206Calculation();

  // Wrap loadRecord for navigation
  const loadRecordWrapped = useCallback(async (id: string) => {
    await loadRecord(id);
  }, [loadRecord]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useD206Navigation(loadRecordWrapped);

  // ── Approval hooks ──
  const {
    approvalHistoryVisible, setApprovalHistoryVisible,
    handleSubmit, handleApprove, handleReject, handleWriteoff, handleViewHistory,
  } = useD206Approval({ record, isEditing, loadRecord: loadRecordWrapped });

  // ── Initial Load ──
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (reviewId) {
          const idx = ids.indexOf(reviewId);
          if (idx >= 0) setCurrentIdx(idx);
          await loadRecordWrapped(reviewId);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWrapped(ids[0]);
        }
      } catch {
        console.error('Failed to load D206 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWrapped, setCurrentIdx]);

  // ── Resolve lookup display names when formData changes ──
  useEffect(() => {
    const fd160_10 = (formData.fd160_10 || '').trim();
    const fd160_11 = (formData.fd160_11 || '').trim();
    const fd160_13 = (formData.fd160_13 || '').trim();

    if (fd160_10) {
      lookupApi.getName('vendor', fd160_10).then(r => setSubcontractorName(r?.name || '')).catch(() => setSubcontractorName(''));
    } else {
      setSubcontractorName('');
    }
    if (fd160_11) {
      lookupApi.getName('purchaseReceipt', fd160_11).then(r => setPurchaseReceiptName(r?.name || '')).catch(() => setPurchaseReceiptName(''));
    } else {
      setPurchaseReceiptName('');
    }
    if (fd160_13) {
      lookupApi.getName('warehouse', fd160_13).then(r => setWarehouseName(r?.name || '')).catch(() => setWarehouseName(''));
    } else {
      setWarehouseName('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fd160_10, formData.fd160_11, formData.fd160_13]);

  useEffect(() => {
    const receiptNo = (formData.fd160_11 || '').trim();
    const warehouse = (formData.fd160_13 || '').trim();
    if (mode === 'add' && receiptNo && warehouse) {
      void importReceiptStock(receiptNo, warehouse);
    }
  }, [mode, formData.fd160_11, formData.fd160_13, importReceiptStock]);

  // ── Mode Actions ──
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      // Save
      await handleSave(mode, async (result: FD160) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fd160_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      // Enter add mode
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearFieldErrors();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd, clearFieldErrors]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除繳庫退出單 ${record.fd160_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWrapped(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWrapped, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  // ── Computed Summary (BR-05) ──
  const { totalQty, totalAmount, taxAmount, totalWithTax } = calcSummary(details);

  const statusBadge = getStatusBadge(record);
  const isFieldDisabled = useCallback(
    (key: string) => isFieldDisabledFn(key, isEditing, mode),
    [isEditing, mode]
  );

  // ── Render ──
  return (
    <PermissionGuard module={Modules.D206} action="query">
    <div className="d206-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>倉庫管理</a>
              <span>/</span>
              <span className="current">繳庫退出單</span>
            </nav>
            <span className="title-separator">—</span>
            <h1>繳庫退出單</h1>
            <span className="badge badge-default">D206</span>
            {statusBadge && (
              <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>
            )}
          </div>
        </div>

        {/* Form Info Bar — 單號顯示 */}
        <div className="form-info-bar">
          <div className="field-group">
            <label>繳庫退出單號</label>
            <input
              type="text"
              value={(getField('fd160_01') as string) || (isEditing && mode === 'add' ? '*新增*' : '')}
              readOnly
              style={{ width: 140 }} data-contract-field="fd160_01"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {['基本資料', '明  細'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 1: 基本資料 */}
          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo
              formData={formData}
              isEditing={isEditing}
              isFieldDisabled={isFieldDisabled}
              getField={getField}
              setField={setField}
              getFieldError={getFieldError}
              totalQty={totalQty}
              totalAmount={totalAmount}
              taxAmount={taxAmount}
              totalWithTax={totalWithTax}
              subcontractorName={subcontractorName}
              purchaseReceiptName={purchaseReceiptName}
              warehouseName={warehouseName}
            />
          </div>

          {/* Tab 2: 明細 */}
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2Detail
              details={details}
              selectedRow={selectedRow}
              isEditing={isEditing}
              totalItems={details.length}
              setSelectedRow={setSelectedRow}
              setDetails={setDetails}
              deleteDetailRow={deleteDetailRow}
              calcDetailAmount={calcDetailAmount}
            />
          </div>
        </div>

        {/* Bottom Action Bar */}
        <SharedActionBar
          mode={mode} isEditing={isEditing} hasRecord={!!record}
          currentIdx={currentIdx} recordCount={allIds.length}
          onNavFirst={() => handleNavTo(0)} onNavPrev={() => handleNavTo(currentIdx - 1)}
          onNavNext={() => handleNavTo(currentIdx + 1)} onNavLast={() => handleNavTo(allIds.length - 1)}
          onAdd={handleAddAction} onModify={handleModifyAction} onDelete={handleDeleteAction}
          onSave={handleAddAction} onCancel={handleModifyAction}
          approval={{
            statusCode: (record?.fd160_80 || '').trim(),
            isApproved: (record?.fd160_80 || '').trim() === 'P',
            isPending: ['D', 'S'].includes((record?.fd160_80 || '').trim()), // 統一碼 D + 舊碼 S 過渡容忍
            isLocked: isLockedForEdit(record),
          }}
          onSubmit={handleSubmit} onApprove={handleApprove}
          onReject={handleReject} onWriteoff={handleWriteoff} onHistory={handleViewHistory}
          onExit={() => navigate('/erp')}
          canCreate={perms.canCreate} canEdit={perms.canEdit}
          canDelete={perms.canDelete} canApprove={perms.canApprove}
          acceptanceActionIds={{
            list: 'PWD206F1-ACT-LIST', get: 'PWD206F1-ACT-GET',
            create: 'PWD206F1-ACT-CREATE', update: 'PWD206F1-ACT-UPDATE',
            delete: 'PWD206F1-ACT-DELETE', submit: 'PWD206F1-ACT-SUBMIT',
            approve: 'PWD206F1-ACT-APPROVE', reject: 'PWD206F1-ACT-REJECT',
            writeoff: 'PWD206F1-ACT-WRITEOFF',
          }}
        />
        <BatchAcceptanceBridge
          manifest={pwd206f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fd160_01 ? [record.fd160_01] : []}
          keyOf={(item) => item.fd160_01}
          queryExact={(key) => d206Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && handleNavTo(0)}
          autoFillDisabled={allIds.length === 0}
          autoFillReason="載入正式 API 已存在的第一筆繳庫退出單作範例，不直接寫業務資料。"
          screenDraft={{ master: formData, details }}
        />

      </div>

      {/* Approval History Modal */}
      {record && (
        <ApprovalHistoryModal
          visible={approvalHistoryVisible}
          orderNo={record.fd160_01}
          onClose={() => setApprovalHistoryVisible(false)}
        />
      )}
    </div>
    </PermissionGuard>
  );
};

export default D206Form;
